import { useNhostClient } from 'Providers/nhost/NhostProvider.tsx';
import { useEffect, useState } from 'react';
import { useCompanyType } from 'utils/hooks';

export const useReportProgress = (assessmentId: string, frameworkRef: string) => {
  const { isGroup } = useCompanyType();
  const [progress, setProgress] = useState('0');
  const nhost = useNhostClient();

  const getProgress = async () => {
    const res = await nhost.functions.post<{ progress: string }>(
      '/esrs/progress/company-progress',
      {
        assessmentId,
        type: isGroup ? 'group' : 'companyReport',
        frameworkRef,
      }
    );

    if (res.body.progress) {
      setProgress(res.body.progress);
    }
  };

  useEffect(() => {
    getProgress();
  }, [isGroup, assessmentId]);
  return progress;
};

export const useDisclosureProgress = ({
  drRef,
  esrsAssessmentId,
  standardRef,
  parentAssessmentId,
  frameworkRef,
}: {
  drRef: string;
  esrsAssessmentId: string;
  standardRef: string;
  parentAssessmentId?: string;
  frameworkRef: string;
}) => {
  const { isGroup } = useCompanyType();
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('0');
  const nhost = useNhostClient();
  const getProgress = async () => {
    setLoading(true);
    const res = await nhost.functions.post<{ progress: string }>(
      '/esrs/progress/disclosure-requirement-progress',
      {
        drRef,
        assessmentId: esrsAssessmentId,
        standardRef,
        parentAssessmentId,
        type: isGroup ? 'group' : 'company',
        frameworkRef,
      }
    );
    if (res.body.progress) {
      setProgress(res.body.progress);
    }
    setLoading(false);
  };

  useEffect(() => {
    getProgress();
  }, [drRef, esrsAssessmentId, standardRef, parentAssessmentId, isGroup]);

  return {
    progress,
    loading,
  };
};

export const useStandardProgress = ({
  standardRef,
  selectedStandardDRs,
  esrsAssessmentId,
  frameworkRef,
}: {
  standardRef: string;
  selectedStandardDRs: string[];
  esrsAssessmentId: string;
  frameworkRef: string;
}) => {
  const { isGroup } = useCompanyType();
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('0');
  const nhost = useNhostClient();

  const getProgress = async () => {
    setLoading(true);
    const res = await nhost.functions.post<{ progress: string }>(
      '/esrs/progress/standard-progress',
      {
        drRefs: selectedStandardDRs,
        assessmentId: esrsAssessmentId,
        standardRef: standardRef,
        type: isGroup ? 'group' : 'company',
        frameworkRef,
      }
    );

    if (res.body.progress) {
      setProgress(res.body.progress);
    }
    setLoading(false);
  };

  useEffect(() => {
    getProgress();
  }, [isGroup, selectedStandardDRs, esrsAssessmentId, standardRef]);

  return {
    progress,
    loading,
  };
};

export const useCategoryProgress = ({
  categoryRef,
  esrsAssessmentId,
  frameworkRef,
}: {
  categoryRef: string;
  esrsAssessmentId: string;
  frameworkRef: string;
}) => {
  const { isGroup } = useCompanyType();
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('0');
  const nhost = useNhostClient();

  const getProgress = async () => {
    setLoading(true);
    const res = await nhost.functions.post<{ progress: string }>(
      '/esrs/progress/category-progress',
      {
        assessmentId: esrsAssessmentId,
        categoryRef: categoryRef ?? '',
        type: isGroup ? 'group' : 'company',
        frameworkRef,
      }
    );

    if (res.body.progress) {
      setProgress(res.body.progress);
    }
    setLoading(false);
  };

  useEffect(() => {
    getProgress();
  }, [isGroup, esrsAssessmentId, categoryRef]);

  return {
    progress,
    loading,
  };
};
